/**
 * Client intake Medplum Bot wrappers (email verification, registration, matching).
 * Provider/Supervisor roles do not receive FHIR Patient create — bots run with scoped policies.
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { EpisodeOfCare, Identifier, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { config } from '../../../config';
import type { IntakeAnswers } from './types';

export const INTAKE_EMAIL_SERVICE_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'intake-email-service',
} as const;

// Env var names kept as INTAKE_REGISTRATION_* for deployment compatibility; the bot was
// renamed to `client-intake-ticketing-master`, so the default identifier value points there.
export const INTAKE_REGISTRATION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'client-intake-ticketing-master',
} as const;

const INTAKE_ACTION_NOT_REIMPLEMENTED =
  'This intake action is not yet reimplemented during the intake overhaul.';

function getIntakeEmailServiceBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_INTAKE_EMAIL_SERVICE_BOT_ID?.trim();
  if (id) {
    return id;
  }
  const system =
    import.meta.env.VITE_MEDPLUM_INTAKE_EMAIL_SERVICE_BOT_IDENTIFIER_SYSTEM?.trim() ??
    INTAKE_EMAIL_SERVICE_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_INTAKE_EMAIL_SERVICE_BOT_IDENTIFIER_VALUE?.trim() ??
    INTAKE_EMAIL_SERVICE_BOT_IDENTIFIER.value;
  return { system, value };
}

function getIntakeRegistrationBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_INTAKE_REGISTRATION_BOT_ID?.trim();
  if (id) {
    return id;
  }
  const system =
    import.meta.env.VITE_MEDPLUM_INTAKE_REGISTRATION_BOT_IDENTIFIER_SYSTEM?.trim() ??
    INTAKE_REGISTRATION_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_INTAKE_REGISTRATION_BOT_IDENTIFIER_VALUE?.trim() ??
    INTAKE_REGISTRATION_BOT_IDENTIFIER.value;
  return { system, value };
}

export interface SendVerificationCodeResult {
  sent: true;
}

export interface MintedPatientLogin {
  accessToken: string;
  refreshToken: string;
  profile: string;
  project: string;
}

export type VerifyEmailAndRegisterResult =
  | { status: 'new-client'; patientId: string; login: MintedPatientLogin }
  | { status: 'existing-client'; patientId: string };

export type IntakeSessionTypeForAvailability = 'intake' | 'consultation';

export interface ProviderAvailabilitySlotDto {
  start: string;
  end: string;
}

export interface GetProviderAvailabilityResult {
  date: string;
  providerId: string;
  sessionType: IntakeSessionTypeForAvailability;
  timeZone: string;
  availableWeekdays: string[];
  slots: ProviderAvailabilitySlotDto[];
}

export interface GetProviderBookableDatesResult {
  providerId: string;
  sessionType: IntakeSessionTypeForAvailability;
  timeZone: string;
  availableWeekdays: string[];
  bookableDates: string[];
}

/** Computed open windows for a provider on one day (no raw calendar exposed). */
export async function getProviderAvailability(
  medplum: MedplumClient,
  input: {
    providerId: string;
    sessionType: IntakeSessionTypeForAvailability;
    date: string;
    timeZone?: string;
    durationMinutes?: number;
    slotStepMinutes?: number;
    minBookingHours?: number;
    maxBookingDays?: number;
    excludeAppointmentId?: string;
    maxSlots?: number;
  },
): Promise<GetProviderAvailabilityResult> {
  return medplum.executeBot(
    getIntakeMatchingBotTarget(),
    {
      action: 'get-provider-availability',
      providerId: input.providerId,
      sessionType: input.sessionType,
      date: input.date,
      timeZone: input.timeZone,
      durationMinutes: input.durationMinutes,
      slotStepMinutes: input.slotStepMinutes,
      minBookingHours: input.minBookingHours,
      maxBookingDays: input.maxBookingDays,
      excludeAppointmentId: input.excludeAppointmentId,
      maxSlots: input.maxSlots,
    },
    ContentType.JSON,
  ) as Promise<GetProviderAvailabilityResult>;
}

/** Dates in the booking window with at least one open slot (single server-side scan). */
export async function getProviderBookableDates(
  medplum: MedplumClient,
  input: {
    providerId: string;
    sessionType: IntakeSessionTypeForAvailability;
    timeZone?: string;
    durationMinutes?: number;
    slotStepMinutes?: number;
    minBookingHours?: number;
    maxBookingDays?: number;
    excludeAppointmentId?: string;
  },
): Promise<GetProviderBookableDatesResult> {
  return medplum.executeBot(
    getIntakeMatchingBotTarget(),
    {
      action: 'get-provider-bookable-dates',
      providerId: input.providerId,
      sessionType: input.sessionType,
      timeZone: input.timeZone,
      durationMinutes: input.durationMinutes,
      slotStepMinutes: input.slotStepMinutes,
      minBookingHours: input.minBookingHours,
      maxBookingDays: input.maxBookingDays,
      excludeAppointmentId: input.excludeAppointmentId,
    },
    ContentType.JSON,
  ) as Promise<GetProviderBookableDatesResult>;
}

/** Send a 6-digit OTP to the user's email (step 5 → 6). No Patient is created. */
export async function sendVerificationCode(
  medplum: MedplumClient,
  input: { email: string },
): Promise<SendVerificationCodeResult> {
  return medplum.executeBot(
    getIntakeEmailServiceBotTarget(),
    {
      action: 'send-verification-code',
      email: input.email,
    },
    ContentType.JSON,
  ) as Promise<SendVerificationCodeResult>;
}

/** Verify OTP, detect returning clients, and create Patient + User for new clients. */
export async function verifyEmailAndRegister(
  medplum: MedplumClient,
  input: {
    email: string;
    code: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    zip?: string;
    /** Defaults to VITE_MEDPLUM_PROJECT_ID (required for public client_credentials intake). */
    projectId?: string;
    /** Practice intake link: Organization id resolved from token — required for Practice → Users. */
    organizationId?: string;
    /** Practice intake URL token (`/intake/:token`); bot resolves org when client cannot search Organization. */
    intakeLinkToken?: string;
    /** Whether the client is high-risk (from screening answers); marks the Patient at creation. */
    isHighRisk?: boolean;
  },
): Promise<VerifyEmailAndRegisterResult> {
  const projectId = input.projectId ?? config.medplum.projectId;
  const clientId = config.medplum.clientId;
  if (!projectId) {
    throw new Error(
      'VITE_MEDPLUM_PROJECT_ID is not set — required for intake registration after email verification.',
    );
  }
  if (!clientId) {
    throw new Error(
      'VITE_MEDPLUM_CLIENT_ID is not set — required for intake session refresh tokens.',
    );
  }
  return medplum.executeBot(
    getIntakeEmailServiceBotTarget(),
    {
      action: 'verify-email-and-register',
      email: input.email,
      code: input.code,
      firstName: input.firstName,
      lastName: input.lastName,
      dateOfBirth: input.dateOfBirth,
      zip: input.zip,
      projectId,
      clientId,
      ...(config.medplum.clientSecret ? { clientSecret: config.medplum.clientSecret } : {}),
      organizationId: input.organizationId,
      intakeLinkToken: input.intakeLinkToken,
      isHighRisk: input.isHighRisk,
    },
    ContentType.JSON,
  ) as Promise<VerifyEmailAndRegisterResult>;
}

/** Create a pilot-tagged Patient after demographics (public intake, before payment). */
export async function createIntakeLeadPatient(
  _medplum: MedplumClient,
  _answers: Pick<IntakeAnswers, 'firstName' | 'lastName' | 'email' | 'dateOfBirth' | 'zip'>,
): Promise<Patient> {
  throw new Error(INTAKE_ACTION_NOT_REIMPLEMENTED);
}

/** Result from intake-lead-patient Bot `create-intake-service-requests` action. */
export interface IntakeServiceRequestPairsResult {
  pairs: Array<{ serviceRequest: ServiceRequest; episodeOfCare: EpisodeOfCare }>;
}

/**
 * Create one ServiceRequest + EpisodeOfCare per therapy code via the intake-lead-patient Bot.
 * Required for public intake completion: the OAuth client does not have ServiceRequest write access.
 */
export async function createIntakeServiceRequestsViaBot(
  _medplum: MedplumClient,
  _input: {
    patientId: string;
    organizationId?: string;
    therapyTypeCodes: string[];
    initialNote?: string;
    stage?: string;
    topics?: string[];
    approaches?: string[];
    providerPreferences?: string[];
    availabilityTimeOfDay?: string[];
    availabilityDays?: string[];
    sessionFormat?: string | null;
    preferredLocationIds?: string[];
    paymentType?: 'insurance' | 'self-pay';
    coverageId?: string;
  },
): Promise<IntakeServiceRequestPairsResult> {
  throw new Error(INTAKE_ACTION_NOT_REIMPLEMENTED);
}

export interface IntakeDraftResourcesResult {
  pairs: Array<{ serviceRequest: ServiceRequest; episodeOfCare: EpisodeOfCare }>;
  reservationExpiresAt: string;
}

export interface ExtendIntakeReservationResult {
  reservationExpiresAt: string;
  updated: { serviceRequests: number; episodesOfCare: number; appointments: number };
}

export interface ReserveReferralSlotResult {
  appointmentId: string;
  referralTaskId: string;
}

export interface PatchIntakeProposedAppointmentResult {
  appointmentId: string;
  start: string;
  end: string;
}

export interface PromoteIntakeSessionResult {
  trackerStage: string;
  promotedServiceRequestIds: string[];
  promotedEpisodeOfCareIds: string[];
  bookedAppointmentIds: string[];
  updatedReferralTaskIds: string[];
  cancelledServiceRequestIds?: string[];
  cancelledEpisodeOfCareIds?: string[];
}

/** True when Medplum indicates a 409 / conflict (e.g. referral capacity). */
export function isReferralSlotConflictError(err: unknown): boolean {
  const s = String(err instanceof Error ? err.message : err).toLowerCase();
  return s.includes('409') || s.includes('conflict');
}

export async function createIntakeDraftResourcesViaBot(
  medplum: MedplumClient,
  input: {
    patientId: string;
    sessionId: string;
    /** Practice Organization id — ServiceRequest.performer on draft SRs. */
    organizationId?: string;
    therapyTypeCodes: string[];
    reservationExpiresAt?: string;
    topics?: string[];
    approaches?: string[];
    providerPreferences?: string[];
    availabilityTimeOfDay?: string[];
    availabilityDays?: string[];
    sessionFormat?: string | null;
    preferredLocationIds?: string[];
    paymentType?: 'insurance' | 'self-pay';
    groupSelection?: 'none' | 'help-find' | 'group';
    requestedGroupId?: string;
    requestedGroupTemplateId?: string;
  },
): Promise<IntakeDraftResourcesResult> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'create-intake-draft-resources',
      patientId: input.patientId,
      sessionId: input.sessionId,
      organizationId: input.organizationId,
      therapyTypeCodes: input.therapyTypeCodes,
      reservationExpiresAt: input.reservationExpiresAt,
      topics: input.topics,
      approaches: input.approaches,
      providerPreferences: input.providerPreferences,
      availabilityTimeOfDay: input.availabilityTimeOfDay,
      availabilityDays: input.availabilityDays,
      sessionFormat: input.sessionFormat,
      preferredLocationIds: input.preferredLocationIds,
      paymentType: input.paymentType,
      groupSelection: input.groupSelection,
      requestedGroupId: input.requestedGroupId,
      requestedGroupTemplateId: input.requestedGroupTemplateId,
    },
    ContentType.JSON,
  ) as Promise<IntakeDraftResourcesResult>;
}

export async function extendIntakeReservationViaBot(
  medplum: MedplumClient,
  input: { sessionId: string; reservationExpiresAt?: string },
): Promise<ExtendIntakeReservationResult> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'extend-intake-reservation',
      sessionId: input.sessionId,
      reservationExpiresAt: input.reservationExpiresAt,
    },
    ContentType.JSON,
  ) as Promise<ExtendIntakeReservationResult>;
}

export async function reserveReferralSlotViaBot(
  medplum: MedplumClient,
  input: {
    referralTaskId: string;
    episodeOfCareId: string;
    sessionId: string;
    practitionerId: string;
    assignmentLabel?: string;
  },
): Promise<ReserveReferralSlotResult> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'reserve-referral-slot',
      referralTaskId: input.referralTaskId,
      episodeOfCareId: input.episodeOfCareId,
      sessionId: input.sessionId,
      practitionerId: input.practitionerId,
      assignmentLabel: input.assignmentLabel,
    },
    ContentType.JSON,
  ) as Promise<ReserveReferralSlotResult>;
}

export async function releaseReferralSlotViaBot(
  medplum: MedplumClient,
  input: { referralTaskId: string; episodeOfCareId: string; sessionId: string },
): Promise<{ released: true }> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'release-referral-slot',
      referralTaskId: input.referralTaskId,
      episodeOfCareId: input.episodeOfCareId,
      sessionId: input.sessionId,
    },
    ContentType.JSON,
  ) as Promise<{ released: true }>;
}

/** Staff manual add-client: hold a proposed appointment without referral Task capacity. */
export async function holdIntakeProviderSlotViaBot(
  medplum: MedplumClient,
  input: {
    episodeOfCareId: string;
    sessionId: string;
    practitionerId: string;
    assignmentLabel?: string;
  },
): Promise<{ appointmentId: string }> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'hold-intake-provider-slot',
      episodeOfCareId: input.episodeOfCareId,
      sessionId: input.sessionId,
      practitionerId: input.practitionerId,
      assignmentLabel: input.assignmentLabel,
    },
    ContentType.JSON,
  ) as Promise<{ appointmentId: string }>;
}

export async function patchIntakeProposedAppointmentViaBot(
  medplum: MedplumClient,
  input: { appointmentId: string; sessionId: string; start: string; end: string },
): Promise<PatchIntakeProposedAppointmentResult> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'patch-intake-proposed-appointment',
      appointmentId: input.appointmentId,
      sessionId: input.sessionId,
      start: input.start,
      end: input.end,
    },
    ContentType.JSON,
  ) as Promise<PatchIntakeProposedAppointmentResult>;
}

export async function promoteIntakeSessionViaBot(
  medplum: MedplumClient,
  input: {
    sessionId: string;
    patientId: string;
    /** Practice Organization id — performer + patient compartment on promote. */
    organizationId?: string;
    paymentType?: 'insurance' | 'self-pay' | null;
    isHighRisk?: boolean;
    /** EpisodeOfCare ids cancelled on the overview — torn down instead of promoted. */
    cancelledEpisodeIds?: string[];
    /** Therapy service-type codes waitlisted at submit (e.g. group with no cohort capacity). */
    waitlistedTherapyTypeCodes?: string[];
    groupSelection?: 'none' | 'help-find' | 'group';
    requestedGroupId?: string;
    requestedGroupTemplateId?: string;
  },
): Promise<PromoteIntakeSessionResult> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'promote-intake-session',
      sessionId: input.sessionId,
      patientId: input.patientId,
      organizationId: input.organizationId,
      paymentType: input.paymentType ?? undefined,
      isHighRisk: input.isHighRisk ?? undefined,
      cancelledEpisodeIds: input.cancelledEpisodeIds,
      waitlistedTherapyTypeCodes: input.waitlistedTherapyTypeCodes,
      groupSelection: input.groupSelection,
      requestedGroupId: input.requestedGroupId,
      requestedGroupTemplateId: input.requestedGroupTemplateId,
    },
    ContentType.JSON,
  ) as Promise<PromoteIntakeSessionResult>;
}

export async function ensurePracticeClientViaBot(
  medplum: MedplumClient,
  input: { patientId: string; organizationId: string },
): Promise<void> {
  await medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'ensure-practice-client',
      patientId: input.patientId,
      organizationId: input.organizationId,
    },
    ContentType.JSON,
  );
}

export interface ProvisionEpisodePanelResult {
  careTeamId: string;
  careTeamName?: string;
}

/** Staff-only: shared panel CareTeam + Patient/$set-accounts (bot admin — not browser FHIR). */
export async function provisionEpisodePanelViaBot(
  medplum: MedplumClient,
  input: { patientId: string; practitionerId: string; organizationId?: string },
): Promise<ProvisionEpisodePanelResult> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'provision-episode-panel',
      patientId: input.patientId,
      practitionerId: input.practitionerId,
      organizationId: input.organizationId,
    },
    ContentType.JSON,
  ) as Promise<ProvisionEpisodePanelResult>;
}

export interface InviteIntakePatientResult {
  invited: boolean;
  patientId: string;
  reason?: 'already-exists';
}

// Env var names kept as INTAKE_MATCHING_* for deployment compatibility; the bot was
// renamed to `client-intake-matching`, so the default identifier value points there.
export const INTAKE_MATCHING_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'client-intake-matching',
} as const;

function getIntakeMatchingBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_INTAKE_MATCHING_BOT_ID?.trim();
  if (id) {
    return id;
  }
  const system =
    import.meta.env.VITE_MEDPLUM_INTAKE_MATCHING_BOT_IDENTIFIER_SYSTEM?.trim() ??
    INTAKE_MATCHING_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_INTAKE_MATCHING_BOT_IDENTIFIER_VALUE?.trim() ??
    INTAKE_MATCHING_BOT_IDENTIFIER.value;
  return { system, value };
}

export interface IntakeMatchingInput {
  action: 'match-providers';
  topics: string[];
  approaches: string[];
  providerPreferences: string[];
  language: string;
  format: string;
  availabilityTimeOfDay: string[];
  availabilityDays: string[];
  dateOfBirth: string;
  zip: string;
  /**
   * Clinical-session HealthcareService ids the provider must be entitled to deliver for the
   * current Episode-of-Care type (intake-only -> [intake], consultation-only -> [consultation],
   * both -> [intake, consultation]). Omit/empty = no service-type filter.
   */
  requiredHealthcareServiceIds?: string[];
}

export interface MatchedProvider {
  id: string;
  name: string;
  credentials: string;
  headshotUrl: string | null;
  gender: 'male' | 'female' | 'other';
  specialties: string[];
  format: ('in-person' | 'virtual')[];
  location: string;
  zip: string;
  acceptsInsurance: boolean;
  shortBio: string;
  longBio: string;
  languages: string[];
  referralTaskId: string;
}

export interface IntakeMatchingResult {
  providers: MatchedProvider[];
  totalWithOpenReferrals: number;
  totalAfterLanguageFilter: number;
  totalAfterServiceTypeFilter: number;
}

/** Match intake providers via referral Tasks + language filter (Step 10). */
export async function matchIntakeProviders(
  medplum: MedplumClient,
  input: IntakeMatchingInput,
): Promise<IntakeMatchingResult> {
  return medplum.executeBot(
    getIntakeMatchingBotTarget(),
    input,
    ContentType.JSON,
  ) as Promise<IntakeMatchingResult>;
}

/** Create portal login + branded setup email (via UserSecurityRequest subscription). */
export async function inviteIntakePatientViaBot(
  _medplum: MedplumClient,
  _input: {
    patientId: string;
    email: string;
    firstName: string;
    lastName: string;
    projectId?: string;
  },
): Promise<InviteIntakePatientResult> {
  throw new Error(INTAKE_ACTION_NOT_REIMPLEMENTED);
}
